/**
 * sl-9qm.5.2: PrismaViewDataSource — GraphQL-backed data source for sealed data
 *
 * Reads data from Prisma tables via GraphQL API.
 * Used for System/Sealed data (ElsterFiling, AuditTrail, Analytics).
 * Server-side filter/sort/pagination via GraphQL query variables.
 * Always read-only.
 *
 * SOLID:
 * - S: Only queries Prisma data via GraphQL
 * - L: Substitutable via IViewDataSource
 */
import { Observable, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import type {
  ColumnInfo,
  DatabaseViewData,
  DataSourceConfig,
  IViewDataSource,
  Row,
} from '../types/view-datasource.types';

/**
 * GraphQL client interface — injected for testability.
 */
export interface ViewGraphQLClient {
  query<T>(query: string, variables?: Record<string, unknown>): Promise<T>;
}

/** GraphQL response shape for view data queries */
interface ViewDataQueryResponse {
  viewData: {
    columns: ColumnInfo[];
    rows: Array<{ id: string; cells: Record<string, unknown> }>;
    totalCount: number;
  };
}

/** GraphQL response shape for single row queries */
interface ViewRowQueryResponse {
  viewRow: { id: string; cells: Record<string, unknown> } | null;
}

/** Default polling interval in milliseconds */
const DEFAULT_POLL_INTERVAL_MS = 30_000;

export class PrismaViewDataSource implements IViewDataSource {
  readonly writable = false;

  constructor(
    private readonly graphqlClient: ViewGraphQLClient,
    private readonly pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS
  ) {}

  getViewData(config: DataSourceConfig): Observable<DatabaseViewData> {
    const variables = this.buildQueryVariables(config);

    const fetchData = async (): Promise<DatabaseViewData> => {
      try {
        const response = await this.graphqlClient.query<ViewDataQueryResponse>(
          VIEW_DATA_QUERY,
          variables
        );
        return {
          columns: response.viewData.columns,
          rows: response.viewData.rows,
          totalCount: response.viewData.totalCount,
          writable: false,
        };
      } catch (err) {
        console.error('[PrismaViewDataSource] Query failed:', err);
        return { columns: [], rows: [], totalCount: 0, writable: false };
      }
    };

    // Polling-based observable: emit immediately, then at interval
    return timer(0, this.pollIntervalMs).pipe(switchMap(() => fetchData()));
  }

  getRow(config: DataSourceConfig, rowId: string): Observable<Row | null> {
    const fetchRow = async (): Promise<Row | null> => {
      try {
        const response = await this.graphqlClient.query<ViewRowQueryResponse>(
          VIEW_ROW_QUERY,
          {
            databaseDocId: config.databaseDocId,
            databaseBlockId: config.databaseBlockId,
            rowId,
          }
        );
        return response.viewRow;
      } catch {
        return null;
      }
    };

    return timer(0, this.pollIntervalMs).pipe(switchMap(() => fetchRow()));
  }

  private buildQueryVariables(
    config: DataSourceConfig
  ): Record<string, unknown> {
    return {
      databaseDocId: config.databaseDocId,
      databaseBlockId: config.databaseBlockId,
      filters: config.filters ?? [],
      sorts: config.sorts ?? [],
      page: config.pagination?.page ?? 1,
      pageSize: config.pagination?.pageSize ?? 50,
      visibleColumns: config.visibleColumns ?? [],
    };
  }
}

/**
 * GraphQL query for view data — server-side filter/sort/pagination.
 * The backend resolver handles Prisma WHERE/ORDER/SKIP/TAKE translation.
 */
const VIEW_DATA_QUERY = `
  query ViewData(
    $databaseDocId: String!
    $databaseBlockId: String!
    $filters: [ViewFilterInput!]
    $sorts: [ViewSortInput!]
    $page: Int
    $pageSize: Int
    $visibleColumns: [String!]
  ) {
    viewData(
      databaseDocId: $databaseDocId
      databaseBlockId: $databaseBlockId
      filters: $filters
      sorts: $sorts
      page: $page
      pageSize: $pageSize
      visibleColumns: $visibleColumns
    ) {
      columns {
        id
        name
        type
        isPrimary
      }
      rows {
        id
        cells
      }
      totalCount
    }
  }
`;

const VIEW_ROW_QUERY = `
  query ViewRow(
    $databaseDocId: String!
    $databaseBlockId: String!
    $rowId: String!
  ) {
    viewRow(
      databaseDocId: $databaseDocId
      databaseBlockId: $databaseBlockId
      rowId: $rowId
    ) {
      id
      cells
    }
  }
`;
